import { CombinedState, createSelector } from '@reduxjs/toolkit'
import {
  useFlag,
  useExperiment as useSharedExperiment,
  useTriggerExperimentEvent as useSharedTriggerExperimentEvent,
  useExperimentTrigger as useSharedExperimentTrigger,
} from '@afterpaytouch/feature'
import { useEffect, useState } from 'react'
import { CheckoutAppSelector, CheckoutAppState, ExperimentsState, useAppSelector } from '../'
import { Experiment, ExperimentValue, Features, KnownExperiment } from '../../model'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useIsHoldoutGroup = (): boolean => {
  return useFlag(Features.CHECKOUT_HOLDOUT_GROUP_EXPERIMENT)
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  // Cast the return type to match the local Experiment type
  return useSharedExperiment(name, fireEvent, customisedExperiment) as unknown as Experiment
}

export const useTriggerExperimentEvent = useSharedTriggerExperimentEvent

export const useExperimentTrigger = useSharedExperimentTrigger

export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (typeof givenExperiment === 'undefined') {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}
